import { useUserEntityPreferences } from "@jugl-web/domain-resources/preferences/hooks/useUserEntityPreferences";
import {
  CrmNotificationCategorySettings,
  TicketNotificationCategorySettings,
} from "@jugl-web/rest-api";
import {
  Checkbox,
  InteractiveContainer,
  Radio,
  DataLoadingWrapper,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { FC, useCallback } from "react";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";

type InboxNotificationPreferenceId = keyof Pick<
  CrmNotificationCategorySettings,
  "managed_by_me" | "managed_by_all" | "managed_by_ai" | "human_handover"
>;

type TicketsNotificationFilter = "all" | "assigned_to_me";

const INBOX_PREFERENCE_IDS: InboxNotificationPreferenceId[] = [
  "managed_by_me",
  "managed_by_all",
  "managed_by_ai",
  "human_handover",
];

const TICKETS_FILTER_IDS: TicketsNotificationFilter[] = [
  "all",
  "assigned_to_me",
];

export const CXWorkspaceNotificationsSettings: FC = () => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { preferences, updatePreferences, isLoading, isError, refetch } =
    useUserEntityPreferences({
      entityId,
    });

  const getInboxPreferenceValue = useCallback(
    (
      crmPreferences: CrmNotificationCategorySettings | undefined,
      id: InboxNotificationPreferenceId
    ) => crmPreferences?.[id] ?? true,
    []
  );

  const getTicketsFilterValue = useCallback(
    (
      ticketPreferences: TicketNotificationCategorySettings | undefined
    ): TicketsNotificationFilter => {
      if (ticketPreferences?.assigned_to_me) {
        return "assigned_to_me";
      }
      return "all";
    },
    []
  );

  const crmPreferences = preferences?.notification_pref?.crm;
  const ticketPreferences = preferences?.notification_pref?.ticket;

  const inboxLabels: Record<InboxNotificationPreferenceId, string> = {
    managed_by_me: t({
      id: "cx-workspace-settings.inbox-managed-by-me",
      defaultMessage: "Conversations managed by me",
    }),
    managed_by_all: t({
      id: "cx-workspace-settings.inbox-managed-by-others",
      defaultMessage: "Conversations managed by other team members",
    }),
    managed_by_ai: t({
      id: "cx-workspace-settings.inbox-managed-by-ai",
      defaultMessage: "Conversations managed by AI Agent",
    }),
    human_handover: t({
      id: "cx-workspace-settings.inbox-ai-escalation",
      defaultMessage: "AI Agent escalate conversation to human response",
    }),
  };

  const ticketsLabels: Record<TicketsNotificationFilter, string> = {
    all: t({
      id: "cx-workspace-settings.tickets-all",
      defaultMessage: "All Tickets",
    }),
    assigned_to_me: t({
      id: "cx-workspace-settings.tickets-assigned-to-me",
      defaultMessage: "Ticket assigned to me",
    }),
  };

  const toggleInboxPreference = useCallback(
    (id: InboxNotificationPreferenceId) => {
      const crm = INBOX_PREFERENCE_IDS.reduce(
        (acc, preferenceId) => ({
          ...acc,
          [preferenceId]: getInboxPreferenceValue(crmPreferences, preferenceId),
        }),
        {} as Pick<
          CrmNotificationCategorySettings,
          InboxNotificationPreferenceId
        >
      );

      crm[id] = !crm[id];

      updatePreferences({
        notification_pref: {
          crm,
        },
      });
    },
    [crmPreferences, getInboxPreferenceValue, updatePreferences]
  );

  const updateTicketsFilter = useCallback(
    (filter: TicketsNotificationFilter) => {
      updatePreferences({
        notification_pref: {
          ticket: {
            all: filter === "all",
            assigned_to_me: filter === "assigned_to_me",
          },
        },
      });
    },
    [updatePreferences]
  );

  return (
    <DataLoadingWrapper
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
    >
      <div className="flex justify-center p-8">
        <div className="flex w-full max-w-[1000px] flex-col gap-10 rounded-3xl border border-solid border-dark-200 p-8">
          <div className="flex flex-col gap-2">
            <span className="font-secondary text-xl leading-[30px] font-semibold text-dark">
              {t({
                id: "cx-workspace-settings.notifications",
                defaultMessage: "🔔 Notifications",
              })}
            </span>
            <span className="font-secondary text-sm leading-[21px] text-dark-700">
              {t({
                id: "cx-workspace-settings.customise-notifications",
                defaultMessage:
                  "Customise which notifications you would like to get:",
              })}
            </span>
          </div>

          <div className="flex flex-col gap-2.5">
            <div className="px-3 py-1.5 font-secondary leading-4 font-semibold text-dark">
              {t({
                id: "cx-workspace-settings.inbox",
                defaultMessage: "Inbox",
              })}
            </div>
            <div className="h-px w-full bg-[#F2F2F2]" />
            {INBOX_PREFERENCE_IDS.map((id) => (
              <div key={id} className="p-3">
                <Checkbox
                  isChecked={getInboxPreferenceValue(crmPreferences, id)}
                  onChange={() => toggleInboxPreference(id)}
                  label={inboxLabels[id]}
                  labelClassName="font-secondary text-sm text-dark"
                />
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2.5">
            <div className="px-3 py-1.5 font-secondary leading-4 font-semibold text-dark">
              {t({
                id: "cx-workspace-settings.tickets",
                defaultMessage: "Tickets",
              })}
            </div>
            <div className="h-px w-full bg-[#F2F2F2]" />

            {TICKETS_FILTER_IDS.map((id) => (
              <InteractiveContainer
                key={id}
                className="flex w-fit cursor-default items-center gap-2 p-3"
                onClick={() => updateTicketsFilter(id)}
              >
                <Radio
                  isChecked={getTicketsFilterValue(ticketPreferences) === id}
                  readOnly
                  size="lg"
                />
                <span className="font-secondary text-sm text-dark">
                  {ticketsLabels[id]}
                </span>
              </InteractiveContainer>
            ))}
          </div>
        </div>
      </div>
    </DataLoadingWrapper>
  );
};
